import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css'
import AdminLayout from '../../layouts/AdminLayout'
import { MdArrowBack, MdDelete, MdSave, MdSend } from 'react-icons/md'
import Switch from 'react-switch'
import Select from 'react-select'
import notify from '../../utils/IANotification'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import SubNavbar from '../../components/ui/SubNavbar'
import StudioEditor from '@grapesjs/studio-sdk/react'
import { rteTinyMce } from '@grapesjs/studio-sdk-plugins'
import '@grapesjs/studio-sdk/style'
import { fetch_all_contact_groups } from '../../api/campaignsAPI'

export default function EmailNewsletterFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  const newsletter_id = new URLSearchParams(window.location.search).get("newsletter_id")
  const isNew = !newsletter_id || newsletter_id === "new"

  const [activeTab, setActiveTab] = useState("content")
  const [newsletterData, setNewsletterData] = useState({
    title: "",
    subject: "",
    html_content: "",
    status: "draft",
    group_ids: [],
    is_everyone: "0",
    scheduled_datetime: "",
  })
  const [groupOptions, setGroupOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const tabs = [
    { id: "content", label: "Email Content" },
    { id: "schedule", label: "Schedule & Recipients" },
  ]

  const STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "scheduled", label: "Scheduled" },
  ]

  useEffect(() => {
    loadGroups()
    if (!isNew) fetchNewsletter()
    else setLoading(false)
  }, [])

  const loadGroups = async () => {
    const { data } = await fetch_all_contact_groups()
    if (data) {
      setGroupOptions((data || []).map(g => ({ value: g.id, label: g.name })))
    }
  }

  const fetchNewsletter = async () => {
    setLoading(true)
    try {
      const res = await fetch(config.endpoint + `/campaigns/email-newsletter/id/${newsletter_id}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      const data = await res.json()
      if (data.status === 200) {
        setNewsletterData({
          ...data.data,
          scheduled_datetime: data.data.scheduled_datetime
            ? new Date(data.data.scheduled_datetime).toISOString().slice(0, 16)
            : "",
        })
      }
    } catch (err) {
      notify({ message: "Failed to load newsletter", type: "error" })
    }
    setLoading(false)
  }

  const saveNewsletter = async () => {
    if (!newsletterData.title?.trim() || !newsletterData.subject?.trim()) {
      notify({ message: "Title and subject are required", type: "error" })
      return
    }
    setSaving(true)
    try {
      const endpoint = isNew
        ? config.endpoint + "/campaigns/email-newsletter"
        : config.endpoint + `/campaigns/email-newsletter/id/${newsletter_id}`
      const method = isNew ? "POST" : "PUT"

      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newsletterData,
          group_ids: newsletterData.group_ids || [],
          scheduled_datetime: newsletterData.scheduled_datetime || null,
        }),
      })
      const data = await res.json()
      if (data.status === 200) {
        notify({ message: "Newsletter saved successfully", type: "success" })
        if (isNew && data.data?.id) {
          navigate(`/admin/campaigns/email-newsletter/details?newsletter_id=${data.data.id}`)
        } else {
          fetchNewsletter()
        }
      } else {
        notify({ message: data.message || "Failed to save newsletter", type: "error" })
      }
    } catch (err) {
      notify({ message: "Failed to save newsletter", type: "error" })
    }
    setSaving(false)
  }

  const sendNewsletter = async () => {
    if (!window.confirm("Send this newsletter now? This action cannot be undone.")) return
    if (isNew || !newsletter_id) {
      notify({ message: "Please save the newsletter first", type: "info" })
      return
    }
    setSaving(true)
    try {
      const res = await fetch(config.endpoint + `/campaigns/email-newsletter/id/${newsletter_id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      const data = await res.json()
      if (data.status === 200) {
        notify({ message: "Newsletter sent successfully", type: "success" })
        fetchNewsletter()
      } else {
        notify({ message: data.message || "Failed to send newsletter", type: "error" })
      }
    } catch (err) {
      notify({ message: "Failed to send newsletter", type: "error" })
    }
    setSaving(false)
  }

  const deleteNewsletter = async () => {
    if (!window.confirm("Delete this newsletter permanently?")) return
    setSaving(true)
    try {
      const res = await fetch(config.endpoint + `/campaigns/email-newsletter/id/${newsletter_id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      const data = await res.json()
      if (data.status === 200) {
        notify({ message: "Newsletter deleted", type: "success" })
        navigate("/admin/campaigns/email-newsletter")
      }
    } catch (err) {
      notify({ message: "Failed to delete newsletter", type: "error" })
    }
    setSaving(false)
  }

  if (loading) return (
    <AdminLayout navSelected={"Email Newsletter"}>
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">Loading...</p>
      </div>
    </AdminLayout>
  )

  return (
    <AdminLayout
      navSelected={"Email Newsletter"}
      toolbar={
        <>
          <button
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            onClick={() => navigate("/admin/campaigns/email-newsletter")}
          >
            <MdArrowBack size={16} />
            Back to Newsletters
          </button>
          <div className="flex gap-2">
            {!isNew && newsletterData.status !== "sent" && (
              <>
                <Button variant="danger" size="sm" icon={MdDelete} onClick={deleteNewsletter} disabled={saving}>Delete</Button>
                <Button variant="secondary" size="sm" icon={MdSend} onClick={sendNewsletter} disabled={saving}>Send Now</Button>
              </>
            )}
            <Button variant="primary" icon={MdSave} onClick={saveNewsletter} disabled={saving || newsletterData.status === "sent"}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-6">
        <SubNavbar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Content Tab */}
        {activeTab === "content" && (
          <div className="space-y-6">
            <Card title="Newsletter Details" subtitle="Basic information about this email newsletter.">
              <div className="space-y-4">
                <div className="input-container">
                  <label className="input-label font-semibold">Newsletter Title (internal)</label>
                  <input
                    type="text"
                    className="text-input"
                    value={newsletterData.title}
                    placeholder="e.g. October 2026 Newsletter"
                    onChange={e => setNewsletterData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="input-container">
                  <label className="input-label font-semibold">Email Subject Line</label>
                  <input
                    type="text"
                    className="text-input"
                    value={newsletterData.subject}
                    placeholder="e.g. October Update from Bedok Methodist Church"
                    onChange={e => setNewsletterData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
              </div>
            </Card>

            <Card title="Email Body" subtitle="Design your email content using the editor below.">
              <div style={{ height: 600 }}>
                <StudioEditor
                  options={{
                    licenseKey: config.grapejs_sdk_key,
                    plugins: [rteTinyMce],
                    storage: { type: "none" },
                    project: {
                      type: "web",
                      default: {
                        pages: [{
                          component: newsletterData.html_content || `<mjml><mj-body><mj-section><mj-column><mj-text font-size="20px">Hello from BMC!</mj-text></mj-column></mj-section></mj-body></mjml>`,
                        }]
                      }
                    }
                  }}
                  onEditor={(editor) => {
                    editor.on("storage:end:store", () => {
                      const html = editor.getHtml()
                      setNewsletterData(prev => ({ ...prev, html_content: html }))
                    })
                  }}
                />
              </div>
            </Card>
          </div>
        )}

        {/* Schedule & Recipients Tab */}
        {activeTab === "schedule" && (
          <div className="space-y-6">
            <Card title="Recipient Targeting" subtitle="Select who will receive this email newsletter.">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div>
                    <label className="font-semibold text-sm text-gray-900">Send to Everyone</label>
                    <p className="text-xs text-gray-500">All contacts will receive this newsletter, ignoring group selection.</p>
                  </div>
                  <Switch
                    checked={newsletterData.is_everyone === "1"}
                    onChange={checked => setNewsletterData(prev => ({ ...prev, is_everyone: checked ? "1" : "0" }))}
                  />
                </div>

                {newsletterData.is_everyone !== "1" && (
                  <div className="input-container">
                    <label className="input-label font-semibold">Target Groups</label>
                    <Select
                      isMulti
                      options={groupOptions}
                      value={groupOptions.filter(g => (newsletterData.group_ids || []).includes(g.value))}
                      onChange={selected => setNewsletterData(prev => ({
                        ...prev,
                        group_ids: selected.map(s => s.value)
                      }))}
                      placeholder="Select recipient groups..."
                    />
                  </div>
                )}
              </div>
            </Card>

            <Card title="Schedule & Status" subtitle="Set when this newsletter should be sent.">
              <div className="space-y-4">
                <div className="input-container">
                  <label className="input-label font-semibold">Status</label>
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(s => s.value === newsletterData.status) || STATUS_OPTIONS[0]}
                    onChange={opt => setNewsletterData(prev => ({ ...prev, status: opt.value }))}
                    isDisabled={newsletterData.status === "sent"}
                  />
                </div>

                <div className="input-container">
                  <label className="input-label font-semibold">Scheduled Date & Time</label>
                  <input
                    type="datetime-local"
                    className="text-input"
                    value={newsletterData.scheduled_datetime || ""}
                    onChange={e => setNewsletterData(prev => ({ ...prev, scheduled_datetime: e.target.value }))}
                    disabled={newsletterData.status === "sent"}
                  />
                </div>

                {newsletterData.status === "sent" && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800 font-medium">
                    ✓ This newsletter has been sent and cannot be modified.
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
